import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Plane } from "lucide-react";

import Header from "@/components/Header";
import Hero from "@/components/Hero";
import AboutSection from "@/components/AboutSection";
import PersonasSection from "@/components/PersonasSection";
import StatsBand from "@/components/StatsBand";
import GeographicCoverage from "@/components/GeographicCoverage";
import FAQSection from "@/components/FAQSection";
import BlogSection from "@/components/BlogSection";
import ConsultationForm from "@/components/ConsultationForm";
import ContactDetails from "@/components/ContactDetails";
import Footer from "@/components/Footer";
import SeniorCarePage from "@/pages/SeniorCarePage";
import RelocationPage from "@/pages/RelocationPage";
import AdminBlogPage from "@/pages/AdminBlogPage";
import ConsultationSuccessPage from "@/pages/ConsultationSuccessPage";
import ConsultationCancelPage from "@/pages/ConsultationCancelPage";
import AdminCalendarPage from "@/pages/AdminCalendarPage";
import AdminBookingsPage from "@/pages/AdminBookingsPage";
import AdminHubPage from "@/pages/AdminHubPage";
import { AdminTokenProvider } from "@/context/AdminTokenContext";

/**
 * ServicesTeaser — light preview linking to the two dedicated service pages.
 * Sits below Hero + About on the landing page.
 */
function ServicesTeaser() {
  const items = [
    {
      to: "/senior-care",
      icon: HeartHandshake,
      eyebrow: "Senior Care",
      title: "In-home care & companionship",
      body:
        "Trained, background-checked caregivers helping seniors stay in their own home — with dignity, safety, and warmth.",
      testId: "teaser-senior-care",
    },
    {
      to: "/relocation",
      icon: Plane,
      eyebrow: "Relocation & Property",
      title: "Seamless return home",
      body:
        "Three white-glove relocation tiers, à la carte add-ons, and professional property oversight for the diaspora.",
      testId: "teaser-relocation",
    },
  ];
  return (
    <section
      id="services-teaser"
      data-testid="services-teaser"
      className="py-24 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <span className="eyebrow">What we do</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-deep)] mt-5 leading-[1.05] font-semibold">
            Two paths, one steady hand.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testId}
                className="group rounded-2xl border border-[var(--gg-border)] bg-white p-8 lg:p-10 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center gap-4">
                  <span className="w-14 h-14 rounded-full bg-[var(--gg-primary)]/10 flex items-center justify-center text-[var(--gg-primary)]">
                    <Icon size={22} strokeWidth={1.6} />
                  </span>
                  <span className="text-[11px] tracking-[0.25em] uppercase text-[var(--gg-primary)] font-semibold">
                    {it.eyebrow}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--gg-deep)] mt-6 font-semibold">
                  {it.title}
                </h3>
                <p className="mt-4 text-[var(--gg-text)] leading-relaxed">
                  {it.body}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--gg-primary)] group-hover:text-[var(--gg-primary-dark)]">
                  Learn more
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Landing() {
  return (
    <div className="App" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <AboutSection />
        <ServicesTeaser />
        <PersonasSection />
        <FAQSection />
        <ConsultationForm />
        <GeographicCoverage />
        <StatsBand />
        <ContactDetails />
      </main>
      <Footer />
      <BlogSection />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AdminTokenProvider>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/senior-care" element={<SeniorCarePage />} />
        <Route path="/relocation" element={<RelocationPage />} />
        <Route path="/admin" element={<AdminHubPage />} />
        <Route path="/admin/blog" element={<AdminBlogPage />} />
        <Route path="/consultation/success" element={<ConsultationSuccessPage />} />
        <Route path="/consultation/cancel" element={<ConsultationCancelPage />} />
        <Route path="/admin/calendar" element={<AdminCalendarPage />} />
        <Route path="/admin/bookings" element={<AdminBookingsPage />} />
      </Routes>
      </AdminTokenProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid #ECE4F3",
            color: "#3D2E5A",
            fontFamily: "Work Sans, sans-serif",
          },
        }}
      />
    </BrowserRouter>
  );
}
